import BaseFormatter from './base'
import { TreeNode, NodeType, EmulationEventVerdict } from '../../types/tree'
import {
  ReportFilterSection,
  ReportFilterSectionType,
  ReportFilterStat,
  ReportPageFilterSettings,
  ReportPageMultiFileFilterSettings,
} from '../../components/filter/types'
import { EmulationFilterStat, EmulationItem } from '../../features/emulation-data/types'
import { mainFileKey, Signal } from '../../types'
import { formatId, getActionCategory } from '../../features/emulation-data/emulation-utils'
import { camelCaseToWords, inc } from 'app/modules/shared/helpers'

/**
 * Format report response data for emulation data page
 */
export default class EmulationDataFormatter extends BaseFormatter {
  /**
   * Format original report data
   *
   * @param data
   * @param report
   */
  format(data: { [key: string]: any }, report: { [key: string]: any }): void {
    let resource: any = this.getResource(data, 'file')
    if (!resource) {
      return
    }

    const filter: ReportPageMultiFileFilterSettings = { files: {} }

    // input file
    const [emulationData, fileFilter] = this.formatResource(resource)
    const result: any = {}

    if (emulationData) {
      emulationData.title = resource.submitName
      result.input = emulationData
      filter.files[mainFileKey] = fileFilter
    }

    // extracted files
    this.formatExtractedFiles(resource, filter, result)

    // downloaded files
    resource = this.getResource(data, 'file-download')
    this.formatDownloadedFiles(resource, filter, result)

    // Emulation files metadata
    if (data.peEmulationFileMetadata.length) {
      result.filesMetadata = data.peEmulationFileMetadata
    }

    // result
    if (Object.keys(result).length) {
      report.emulation_data = {
        data: result,
        filter: filter,
      }
    }
  }

  formatExtractedFiles(resource: any, filter: ReportPageMultiFileFilterSettings, result: any): void {
    const extractedFiles = resource.extractedFiles
    if (extractedFiles?.length) {
      const extracted: { [fileKey: string]: any } = {}

      for (const file of extractedFiles) {
        const fileKey = file.digests['SHA-256']
        const [emulationData, fileFilter] = this.formatResource(file)

        if (emulationData) {
          filter.files[fileKey] = fileFilter
          emulationData.title = fileKey
          extracted[fileKey] = emulationData
        }
      }

      if (Object.keys(extracted).length) {
        result.extracted = extracted
      }
    }
  }

  formatDownloadedFiles(resource: any, filter: ReportPageMultiFileFilterSettings, result: any): void {
    const downloadedFiles = resource?.fileDownloadResults
    if (downloadedFiles) {
      const downloaded: { [fileKey: string]: any } = {}

      for (const file of downloadedFiles) {
        const fileKey = file.digests['SHA-256']
        const [emulationData, fileFilter] = this.formatResource(file)

        if (emulationData) {
          filter.files[fileKey] = fileFilter
          emulationData.title = fileKey
          downloaded[fileKey] = emulationData
        }
      }

      if (Object.keys(downloaded).length) {
        result.downloaded = downloaded
      }
    }
  }

  formatResource(resource: { [key: string]: any }): any {
    const hasEmulationMetaData = resource?.emulationMetaData
    if (!resource?.emulationData?.length) {
      return []
    }

    let meta = {}
    if (hasEmulationMetaData) {
      const metaData = { ...resource?.emulationMetaData }
      const functionCount = this.formatFunctionCount(metaData)

      if (functionCount) {
        metaData.Overview.FunctionCount = functionCount
      }

      meta = metaData
    }

    const [emulationData, filter] = this.formatItems(resource.emulationData, resource.signalGroupsByID)
    const result: any = {
      data: emulationData,
      tree: this.makeTree(emulationData as any),
      meta: meta,
    }

    if (resource?.emulationExitCode !== undefined) {
      result.exitCode = resource?.emulationExitCode
    }

    return [result, filter]
  }

  formatFunctionCount(metaData: { [key: string]: any }): null | { [key: string]: any } {
    const array = []
    for (const key in metaData.Overview?.FunctionCount) {
      array.push({ index: key, value: metaData.Overview.FunctionCount[key] as number })
    }

    if (!array.length) {
      return null
    }

    array.sort(function (a, b) {
      return a.value - b.value
    })

    const sortedFunctionCount: { [key: string]: any } = {}

    for (const item of array) {
      sortedFunctionCount[item.index] = item.value
    }

    return sortedFunctionCount
  }

  formatItems(
    data: { [key: string]: any }[],
    signalGroups?: { [key: string]: any }
  ): [{ [key: string]: any }, ReportPageFilterSettings] {
    const sectionsCache: { [key: string]: boolean } = {}
    const sections = this.getFilterSectionsTmpl()
    const stat = this.getFilterStatTmpl()

    const formatted = data.map((emulationItem) => {
      const item: EmulationItem = { ...emulationItem } as EmulationItem
      item.category = getActionCategory(item.action)

      if (item.additionalInformation?.Command) {
        item.commandLowerForFilter = item.additionalInformation?.Command.toLocaleLowerCase()
      }

      // Match emulation event with threat indicators to add verdict information
      if (item.emulationEventID && signalGroups) {
        const verdictInfo = this.findVerdictForEmulationEvent(item.emulationEventID, signalGroups)

        if (verdictInfo) {
          item.verdictInfo = verdictInfo
        }
      }

      this.gatherFilterSections(item, sections, sectionsCache)
      this.gatherFilterStat(item, stat)
      this.splitApiCallArguments(item)

      return item
    })

    const sorted = this.fixNodesOrder(formatted)

    return [sorted, { sections: Object.values(sections), stat }]
  }

  gatherFilterSections(
    item: EmulationItem,
    sections: { [sectionName: string]: ReportFilterSection },
    sectionsCache: { [key: string]: boolean }
  ) {
    if (typeof sectionsCache[item.category] === 'undefined') {
      sections.category.items.push({ value: item.category, name: camelCaseToWords(item.category) })
      sectionsCache[item.category] = true
    }
    if (typeof sectionsCache[item.action + '-action'] === 'undefined') {
      sections.type.items.push({ value: item.action, name: camelCaseToWords(item.action) })
      sectionsCache[item.action + '-action'] = true
    }

    if (item.source && typeof sectionsCache[item.source] === 'undefined') {
      sections.source.items.push({ value: item.source })
      sectionsCache[item.source] = true
    }
  }

  gatherFilterStat(item: EmulationItem, stat: ReportFilterStat) {
    stat.types.category[item.category] = inc(stat.types.category[item.category])
    stat.types.type[item.action] = inc(stat.types.type[item.action])
    item.source && (stat.types.source[item.source] = inc(stat.types.source[item.source]))
    stat.total++
  }

  getFilterSectionsTmpl() {
    const sections: { [sectionName: string]: ReportFilterSection } = {
      search: { type: ReportFilterSectionType.Search, name: 'search', items: [], placeholder: 'search' },
      category: { type: ReportFilterSectionType.Flags, name: 'category', items: [] },
      type: { type: ReportFilterSectionType.Flags, name: 'type', items: [] },
      source: { type: ReportFilterSectionType.Flags, name: 'source', items: [] },
    }

    return sections
  }

  getFilterStatTmpl() {
    const stat: EmulationFilterStat = {
      types: { category: {}, type: {}, source: {} },
      total: 0,
    }

    return stat
  }

  splitApiCallArguments(item: { [key: string]: any }): void {
    if (item.additionalInformation?.PCode) {
      delete item.additionalInformation.PCode.error
      delete item.additionalInformation.PCode.Error
    }
    if ('action' in item && item['action'] === 'CallAPI') {
      if (item.additionalInformation?.Arguments) {
        const args = item.additionalInformation.Arguments
        if (Array.isArray(args)) {
          item.additionalInformation.Arguments = args.map((arg: string) => this.parseArgs(`${arg}`))
        } else {
          item.additionalInformation.Arguments = this.parseArgs(`${args}`)
        }
      }
    }
  }

  parseArgs(args: string): { [key: string]: any }[] {
    const parts = args.split(',').map((item) => item.trim())
    const result: { [key: string]: any }[] = []

    parts.forEach((part, idx) => {
      part = part.replace(/^ByVal\s+/, '')

      if (part.startsWith('(') && part.endsWith(')')) {
        // for key-value pairs, get value for its key
        const [key, value] = this.getKeyValue(part)
        result.push({ name: `Param ${idx++}`, ...this.getValue(key, value) })
      } else if (/^[-+]?\d+\.\d+$/.test(part)) {
        // for value only args, try to determine its type from the value
        result.push({ name: 'Float', value: part })
      } else if (/^[-+]?\d+$/.test(part)) {
        result.push({ name: 'Long', value: Number.parseInt(part) })
      } else {
        result.push({ name: 'String', value: part })
      }
    })

    return result
  }

  /**
   * @name getKeyValue
   * @param input string in format "(key value)" or "key value"
   * @returns [key value]
   */
  getKeyValue(input: string): string[] {
    const content = input.startsWith('(') && input.endsWith(')') ? input.substring(1, input.length - 1) : input
    const spacePos = content.indexOf(' ')
    const key = content.substring(0, spacePos)
    const value = content.substring(spacePos + 1, content.length)
    return [key, value]
  }

  /**
   * @name getValue
   * @param key value type
   * @param value value have different format for its type
   * @returns
   */
  getValue(key: string, value: string): { [key: string]: any } {
    switch (key) {
      case 'StringValue':
        return { name: 'String', value }
      case 'ConcatedStringValue': {
        // for ConcatedStringValue, concatenate all values connected using + sign
        const values = value.split(' + ').map((item) => item.trim())
        let result = ''
        // concatenate values only
        for (const val of values) {
          const [k, v] = this.getKeyValue(val)
          const { value } = this.getValue(k, v)
          result += value
        }

        return { name: 'String', value: result }
      }
      case 'VbValue': {
        const [subKey, subValue] = this.getKeyValue(value)
        if (subKey === 'Long' || subKey === 'Integer') {
          return { name: subKey, value: Number.parseInt(subValue) }
        } else if (subKey === 'UDT') {
          /*
              format: (UDT Book1.INFO) UDT(INFO\n
                Key1 = Value1\n
                ...
                Keyn = Valuen
              )
            */
          if (!subValue.startsWith('(')) {
            if (subValue.startsWith('UDT(')) {
              return this.parseUDT(subValue)
            } else {
              return { name: subKey, value: subValue }
            }
          }

          const spacePos = subValue.indexOf(')')
          const udtValue = subValue.substring(spacePos + 1).trim()
          return this.parseUDT(udtValue)
        } else {
          return { name: subKey, value: subValue }
        }
      }
      default:
        return { name: key, value }
    }
  }

  /**
   * @name parseUDT
   * @param value UDT(INFO\nKey1 = Value1\n...\nKeyn = Valuen)
   * @returns { key1: value1, ..., keyn: valuen }
   */
  parseUDT(value: string): { [key: string]: any } {
    if (!value.startsWith('UDT(') || !value.endsWith(')')) {
      return {}
    }

    value = value.substring(4, value.length - 1)
    const parts = value.split('\n')
    if (parts.length < 1) return {}

    const name = parts[0]
    const args = parts.slice(1)
    const result = []
    for (const arg of args) {
      const eqPos = arg.indexOf('=')
      if (eqPos < 0) continue
      const key = arg.substring(0, eqPos).trim()
      const value = arg.substring(eqPos + 1).trim()

      if (value.startsWith('(') && value.endsWith(')')) {
        const [k, v] = this.getKeyValue(value)
        const { name: type, value: val } = this.getValue(k, v)
        result.push({ name: key, type, value: val })
      } else {
        result.push({ name: key, type: 'String', value: arg })
      }
    }

    return { name, value: result }
  }

  getNodeType(item: { [key: string]: any }): NodeType {
    const actions: { [key: string]: NodeType } = {
      // Process
      StartProcess: NodeType.process,
      // File
      CreateFile: NodeType.file,
      ShellCreatedFile: NodeType.file,
      WriteTextFile: NodeType.file,
      WriteBinaryFile: NodeType.file,
      CopyFile: NodeType.file,
      MoveFile: NodeType.file,
      CopyProgram: NodeType.file,
      MoveProgram: NodeType.file,
      // Execute
      ExecuteSchedulerJob: NodeType.execute,
      ExecQuery: NodeType.execute,
      // Other
      HttpRequest: NodeType.network,
      ExecuteScript: NodeType.script,
    }

    return actions[item.action] ?? NodeType.other
  }

  // Fix nodes order - first one should be root process node
  fixNodesOrder(data: { [key: string]: any }[]): { [key: string]: any }[] {
    if (!data?.length) {
      return data
    }

    const firstNode = data[0]
    if (this.getNodeType(firstNode) === NodeType.process) {
      return data
    }

    const result: any[] = []
    for (const item of data) {
      const type = this.getNodeType(item)
      const isRoot = type === NodeType.process && item.additionalInformation?.ParentPid === '00000000'
      isRoot ? result.unshift(item) : result.push(item)
    }

    return result
  }

  makeTree(data: any[]): TreeNode | null {
    let root: TreeNode | null = null
    const urls = this.collectNetworUrls(data)

    let parent: TreeNode | null = null
    let idx = 1

    for (const item of data) {
      const node: TreeNode = this.createTreeNode(item, idx++)

      if (this.shouldSkipTreeNode(node, item, urls)) {
        continue
      }

      const attachResult = this.attachNodeToTree(node, item, parent, root)
      if (attachResult.shouldBreak) {
        break
      }
      parent = attachResult.parent
      root = attachResult.root
    }

    return root
  }

  collectNetworUrls(data: any[]): string[] {
    const urls: string[] = []

    for (const item of data) {
      const type = this.getNodeType(item)

      // Gather network URLs
      if (type === NodeType.network) {
        const url = item.additionalInformation?.URI
        if (!urls.includes(url)) {
          urls.push(url)
        }
      }
    }

    return urls
  }

  createTreeNode(item: any, idx: number) {
    const node: TreeNode = {
      id: `${idx}`,
      parent: '',
      type: this.getNodeType(item),
      action: item.action,
      interesting: item.interesting ?? false,
      source: item.source,
      extra: { nodeId: formatId(item.dataUUID) },
      original: item,
      childs: [],
    }

    if (item.description) {
      node.extra.description = item.description
    }

    if (item.additionalInformation) {
      node.extra = { ...item.additionalInformation, ...node.extra }
    }

    if (item.verdictInfo) {
      node.verdictInfo = item.verdictInfo
    }

    if (
      item.action === 'ShellCreatedFile' &&
      item.additionalInformation?.Command &&
      typeof item.additionalInformation?.Command === 'string' &&
      !node.extra?.File
    ) {
      const args = item.additionalInformation.Command.split(' ')
      if (args.length > 1) {
        node.extra.File = args[1]
      }
    }

    return node
  }

  shouldSkipTreeNode(node: TreeNode, item: any, urls: string[]): boolean {
    if (node.type !== NodeType.other) {
      return false
    }

    if (!!item.File || !!item.additionalInformation?.File) {
      node.type = NodeType.file
      node.extra.File = item.File ?? item.additionalInformation?.File
      return false
    }

    if (node.extra.URI) {
      node.type = NodeType.network
      return false
    }

    if (this.isUrlDownloadToFileCall(node)) {
      node.type = NodeType.network
      const uri = this.getUrlToDownloadFileUri(item)
      if (uri) {
        node.extra.URI = uri
      }

      return urls.includes(node.extra.URI)
    }

    if (this.isGetHostByNameCall(node)) {
      node.type = NodeType.network
      const uri = this.getHostByNameUri(item)
      if (uri) {
        node.extra.URI = uri
      }

      if (node.extra.URI) {
        const slashPos = node.extra.URI.indexOf('/')
        if (slashPos > 0) {
          node.extra.URI = node.extra.URI.substring(0, slashPos)
        }
      }
      return false
    }

    return true
  }

  getUrlToDownloadFileUri(item: any) {
    if (Array.isArray(item.additionalInformation?.Arguments)) {
      const args = item.additionalInformation.Arguments
      if (args.length > 1) {
        return typeof args[1] === 'string' ? args[1] : args[1].value
      }
    } else {
      const args = item.additionalInformation?.Arguments?.split(',')
      if (args && args.length > 1) {
        return args[1]
      }
    }
    return undefined
  }

  getHostByNameUri(item: any) {
    const args = item.additionalInformation?.Arguments
    if (Array.isArray(args)) {
      if (typeof args[0] === 'string') {
        return args[0]
      } else {
        return args[0].value
      }
    }

    return undefined
  }

  isUrlDownloadToFileCall(node: TreeNode): boolean {
    return (
      node.action === 'CallAPI' &&
      (node.extra.Alias?.startsWith('URLDownloadToFile') || node.extra.Entrance?.startsWith('URLDownloadToFile'))
    )
  }

  isGetHostByNameCall(node: TreeNode): boolean {
    return (
      node.action === 'CallAPI' && (node.extra.Alias === 'gethostbyname' || node.extra.Entrance === 'gethostbyname')
    )
  }

  attachNodeToTree(node: TreeNode, item: any, parent: TreeNode | null, root: TreeNode | null) {
    if (node.type !== NodeType.process) {
      if (!parent) {
        return { root, parent, shouldBreak: true }
      }

      node.parent = parent.id
      parent.childs.push(node)

      return { root, parent, shouldBreak: false }
    }

    if (item.additionalInformation) {
      node.id = item.additionalInformation.Pid
      const parentPid = item.additionalInformation.ParentPid
      if (parentPid === '00000000') {
        if (root) {
          parent = root
          node.parent = parent.id
          parent.childs.push(node)
          parent = node
        } else {
          parent = root = node
        }
      } else {
        parent = this.searchNode(root, parentPid)
        if (!parent) {
          return { root, parent, shouldBreak: true }
        }

        node.parent = parent.id
        parent.childs.push(node)
        parent = node
      }
    }

    return { root, parent, shouldBreak: false }
  }

  searchNode(root: TreeNode | null, pid: string): TreeNode | null {
    if (root?.type !== NodeType.process) return null

    if (root.extra.Pid === pid) return root

    for (const child of root.childs) {
      const node = this.searchNode(child, pid)
      if (node) return node
    }

    return null
  }

  /**
   * Find verdict information for an emulation event by matching emulationEventID with signals
   */
  findVerdictForEmulationEvent(
    emulationEventID: string,
    signalGroups: { [key: string]: any }
  ): EmulationEventVerdict | null {
    for (const group of Object.values(signalGroups)) {
      if (!group.signals) {
        continue
      }

      const matchingSignal = group.signals.find((signal: Signal) => signal.emulationEventID === emulationEventID)
      if (matchingSignal) {
        return {
          verdict: group.verdict?.verdict?.toLowerCase(),
          signalId: group.identifier,
          signalDescription: group.description,
        }
      }
    }

    return null
  }
}
